import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { LoadingState, ErrorState } from "@/components/shared/States";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2 } from "lucide-react";

const STEPS = ["Company details", "Contacts", "Properties & Units", "Compliance notes", "Review & Submit"];

const emptyPayload = { company: { name: "", region: "" }, contacts: [], properties: [], compliance_notes: "" };

export default function SupplierOnboarding() {
  const [supplierId, setSupplierId] = useState(null);
  const [submissionId, setSubmissionId] = useState(null);
  const [step, setStep] = useState(1);
  const [payload, setPayload] = useState(emptyPayload);
  const [history, setHistory] = useState(null);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/supplier/overview").then(async ({ data }) => {
      if (!data.supplier) return setError(true);
      setSupplierId(data.supplier.id);
      const { data: mine } = await api.get("/submissions/mine", { params: { supplier_id: data.supplier.id } });
      setHistory(mine);
      const draft = mine.items.find((s) => ["DRAFT", "INFO_REQUESTED"].includes(s.status));
      if (draft) {
        setSubmissionId(draft.id);
        setStep(draft.step);
        setPayload({ ...emptyPayload, ...draft.payload });
      } else {
        setPayload({ ...emptyPayload, company: { name: data.supplier.name, region: "" } });
      }
    }).catch(() => setError(true));
  }, []);

  const saveDraft = async (nextStep) => {
    setSaving(true);
    try {
      const { data } = await api.post("/submissions", { supplier_id: supplierId, submission_type: "ONBOARDING", step: nextStep, payload });
      setSubmissionId(data.id);
      toast.success("Draft saved");
      if (nextStep) setStep(nextStep);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save draft"); }
    setSaving(false);
  };

  const submit = async () => {
    await saveDraft(5);
    try {
      await api.post(`/submissions/${submissionId}/submit`);
      toast.success("Submitted for Jigsaw review");
      const { data: mine } = await api.get("/submissions/mine", { params: { supplier_id: supplierId } });
      setHistory(mine);
      setSubmissionId(null);
      setStep(1);
      setPayload(emptyPayload);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit"); }
  };

  if (error) return <ErrorState testId="supplier-onboarding-error" />;
  if (!supplierId) return <LoadingState testId="supplier-onboarding-loading" />;

  const addContact = () => setPayload({ ...payload, contacts: [...payload.contacts, { full_name: "", email: "", phone: "", role_title: "" }] });
  const updateContact = (idx, field, value) => {
    const contacts = [...payload.contacts];
    contacts[idx] = { ...contacts[idx], [field]: value };
    setPayload({ ...payload, contacts });
  };
  const removeContact = (idx) => setPayload({ ...payload, contacts: payload.contacts.filter((_, i) => i !== idx) });

  const addProperty = () => setPayload({ ...payload, properties: [...payload.properties, { name: "", address_line1: "", city: "", postcode: "", property_type: "", units: [] }] });
  const updateProperty = (idx, field, value) => {
    const properties = [...payload.properties];
    properties[idx] = { ...properties[idx], [field]: value };
    setPayload({ ...payload, properties });
  };
  const removeProperty = (idx) => setPayload({ ...payload, properties: payload.properties.filter((_, i) => i !== idx) });
  const addUnit = (pIdx) => {
    const properties = [...payload.properties];
    properties[pIdx].units = [...(properties[pIdx].units || []), { unit_type: "", capacity: 1 }];
    setPayload({ ...payload, properties });
  };
  const updateUnit = (pIdx, uIdx, field, value) => {
    const properties = [...payload.properties];
    properties[pIdx].units[uIdx] = { ...properties[pIdx].units[uIdx], [field]: value };
    setPayload({ ...payload, properties });
  };

  return (
    <div data-testid="supplier-onboarding-page" className="space-y-6">
      <div>
        <h1 className="text-[24px] font-semibold leading-tight tracking-[-0.01em] text-jigsaw-navy">Onboarding &amp; Updates</h1>
        <p className="text-sm text-jigsaw-muted">Nothing here changes your live profile until Jigsaw staff approve it.</p>
      </div>

      <ol className="flex flex-wrap items-center gap-x-2 gap-y-2 rounded-[10px] border border-jigsaw-border bg-white px-4 py-3" data-testid="onboarding-stepper" aria-label="Onboarding steps">
        {STEPS.map((s, idx) => {
          const n = idx + 1, done = n < step, current = n === step;
          return (
            <li key={s} className="flex items-center gap-2" aria-current={current ? "step" : undefined}>
              <span className={`flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold ${done ? "bg-jigsaw-success/15 text-jigsaw-success" : current ? "bg-jigsaw-orange text-white" : "border border-jigsaw-border text-jigsaw-muted"}`}>{done ? "✓" : n}</span>
              <span className={`text-[13px] ${current ? "font-semibold text-jigsaw-navy" : "text-jigsaw-muted"}`}>{s}</span>
              {idx < STEPS.length - 1 && <span className="mx-1 hidden h-px w-5 bg-jigsaw-border sm:block" />}
            </li>
          );
        })}
      </ol>

      <Card className="border-jigsaw-border">
        <CardContent className="space-y-4 p-5">
          <header className="mb-4 border-b border-jigsaw-border pb-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-jigsaw-muted">Step {step} of {STEPS.length}</p>
            <h2 className="text-[18px] font-semibold text-jigsaw-navy">{STEPS[step - 1]}</h2>
            <p className="text-[13px] text-jigsaw-muted">{["Who you are as a supplier organisation.", "People Jigsaw can contact about placements.", "Each property and the room types you can offer.", "Anything Jigsaw should know about your certificates and checks.", "Confirm and send for review."][step - 1]}</p>
          </header>
          {step === 1 && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div><Label>Company name</Label><Input data-testid="onboarding-company-name-input" value={payload.company.name} onChange={(e) => setPayload({ ...payload, company: { ...payload.company, name: e.target.value } })} /></div>
              <div><Label>Region</Label><Input data-testid="onboarding-company-region-input" value={payload.company.region} onChange={(e) => setPayload({ ...payload, company: { ...payload.company, region: e.target.value } })} /></div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              {payload.contacts.map((c, idx) => (
                <fieldset key={idx} data-testid={`onboarding-contact-${idx}`} className="rounded-[10px] border border-jigsaw-border p-4">
                  <div className="mb-3 flex items-center justify-between"><legend className="text-[13px] font-semibold text-jigsaw-navy">Contact {idx + 1}</legend><Button variant="outline" size="sm" aria-label={`Remove contact ${idx + 1}`} onClick={() => removeContact(idx)}><Trash2 className="mr-1 h-3.5 w-3.5" /> Remove</Button></div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div><Label>Full name</Label><Input value={c.full_name} onChange={(e) => updateContact(idx, "full_name", e.target.value)} /></div>
                    <div><Label>Role</Label><Input value={c.role_title} onChange={(e) => updateContact(idx, "role_title", e.target.value)} /></div>
                    <div><Label>E-mail</Label><Input type="email" value={c.email} onChange={(e) => updateContact(idx, "email", e.target.value)} /></div>
                    <div><Label>Phone</Label><Input value={c.phone} onChange={(e) => updateContact(idx, "phone", e.target.value)} /></div>
                  </div>
                </fieldset>
              ))}
              <Button data-testid="onboarding-add-contact-button" size="sm" variant="outline" onClick={addContact}><Plus className="mr-1 h-3.5 w-3.5" /> Add contact</Button>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              {payload.properties.map((p, pIdx) => (
                <fieldset key={pIdx} data-testid={`onboarding-property-${pIdx}`} className="space-y-3 rounded-[10px] border border-jigsaw-border p-4">
                  <div className="flex items-center justify-between"><legend className="text-[13px] font-semibold text-jigsaw-navy">Property {pIdx + 1}</legend><Button variant="outline" size="sm" aria-label={`Remove property ${pIdx + 1}`} onClick={() => removeProperty(pIdx)}><Trash2 className="mr-1 h-3.5 w-3.5" /> Remove</Button></div>
                  <div className="grid gap-3 sm:grid-cols-3">
                    <div><Label>Property name</Label><Input value={p.name} onChange={(e) => updateProperty(pIdx, "name", e.target.value)} /></div>
                    <div><Label>City</Label><Input value={p.city} onChange={(e) => updateProperty(pIdx, "city", e.target.value)} /></div>
                    <div><Label>Postcode</Label><Input className="font-mono-ref" value={p.postcode} onChange={(e) => updateProperty(pIdx, "postcode", e.target.value)} /></div>
                  </div>
                  <div className="space-y-2 rounded-md bg-jigsaw-bg/60 p-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-jigsaw-muted">Units</p>
                    {(p.units || []).map((u, uIdx) => (
                      <div key={uIdx} className="grid gap-2 sm:grid-cols-2">
                        <div><Label className="text-[12px]">Unit type</Label><Input value={u.unit_type} onChange={(e) => updateUnit(pIdx, uIdx, "unit_type", e.target.value)} /></div>
                        <div><Label className="text-[12px]">Capacity</Label><Input type="number" min={1} value={u.capacity} onChange={(e) => updateUnit(pIdx, uIdx, "capacity", Number(e.target.value))} /></div>
                      </div>
                    ))}
                    <Button size="sm" variant="outline" onClick={() => addUnit(pIdx)}><Plus className="mr-1 h-3.5 w-3.5" /> Add unit</Button>
                  </div>
                </fieldset>
              ))}
              <Button data-testid="onboarding-add-property-button" size="sm" variant="outline" onClick={addProperty}><Plus className="mr-1 h-3.5 w-3.5" /> Add property</Button>
            </div>
          )}

          {step === 4 && (
            <div><Label>Compliance notes</Label><Textarea data-testid="onboarding-compliance-notes-input" rows={5} value={payload.compliance_notes} onChange={(e) => setPayload({ ...payload, compliance_notes: e.target.value })} placeholder="Anything Jigsaw should know about your certifications" /></div>
          )}

          {step === 5 && (
            <div data-testid="onboarding-review" className="space-y-4">
              <p className="text-sm text-jigsaw-muted">Check each section before submitting. Jigsaw staff review every submission before anything changes on your live profile.</p>
              {[
                { n: 1, title: "Company details", rows: [["Name", payload.company.name], ["Region", payload.company.region]] },
                { n: 2, title: "Contacts", rows: payload.contacts.length ? payload.contacts.map((c) => [c.full_name || "Unnamed contact", [c.role_title, c.email, c.phone].filter(Boolean).join(" · ") || "—"]) : [["No contacts added", "—"]] },
                { n: 3, title: "Properties & units", rows: payload.properties.length ? payload.properties.map((pr) => [pr.name || "Unnamed property", `${[pr.address_line1, pr.city, pr.postcode].filter(Boolean).join(", ") || "Address not given"} · ${pr.units.length} unit${pr.units.length === 1 ? "" : "s"}`]) : [["No properties added", "—"]] },
                { n: 4, title: "Compliance notes", rows: [["Notes", payload.compliance_notes || "—"]] },
              ].map((sec) => (
                <section key={sec.n} data-testid={`onboarding-review-section-${sec.n}`} className="rounded-[10px] border border-jigsaw-border">
                  <header className="flex items-center justify-between border-b border-jigsaw-border px-4 py-2.5">
                    <h3 className="text-[14px] font-semibold text-jigsaw-navy">{sec.title}</h3>
                    <button type="button" data-testid={`onboarding-review-edit-${sec.n}`} onClick={() => setStep(sec.n)} className="text-[13px] text-jigsaw-link hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jigsaw-navy/30">Edit</button>
                  </header>
                  <dl className="divide-y divide-jigsaw-border">
                    {sec.rows.map(([k, v], i) => <div key={i} className="grid grid-cols-1 gap-1 px-4 py-2 text-sm sm:grid-cols-[180px_1fr]"><dt className="text-jigsaw-muted">{k}</dt><dd className="text-jigsaw-navy">{v || "—"}</dd></div>)}
                  </dl>
                </section>
              ))}
              <p className="text-xs text-jigsaw-muted">Submitting sends this to Jigsaw for approval. You can keep saving drafts until then.</p>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" disabled={step === 1} onClick={() => setStep(step - 1)}>Back</Button>
        <div className="flex gap-2">
          <Button data-testid="onboarding-save-draft-button" variant="outline" disabled={saving} onClick={() => saveDraft(step)}>Save draft</Button>
          {step < 5 ? (
            <Button data-testid="onboarding-next-button" onClick={() => saveDraft(step + 1)} className="bg-jigsaw-orange text-white hover:bg-jigsaw-orangeDark">Next</Button>
          ) : (
            <Button data-testid="onboarding-submit-button" onClick={submit} className="bg-jigsaw-orange text-white hover:bg-jigsaw-orangeDark">Submit for review</Button>
          )}
        </div>
      </div>

      {history && history.items.length > 0 && (
        <Card className="border-jigsaw-border">
          <CardHeader><CardTitle className="text-base text-jigsaw-navy">Submission history</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {history.items.map((s) => (
              <div key={s.id} data-testid={`onboarding-history-row-${s.id}`} className="flex items-center justify-between rounded-md border border-jigsaw-border p-2 text-sm">
                <span className="text-jigsaw-navy">{s.submission_type}</span>
                <StatusBadge status={s.status} />
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
